import React, { useCallback, useEffect, useState, useRef } from "react";
import HeaderDashboard from "../components/layout/header/HeaderDashboard";
import LoginAsUser from "../components/loginasuser/LoginAsUser";
import "./../assets/scss/accountguarntee.scss";
import { Link, useSearchParams } from "react-router-dom";
import { FaCheckCircle, FaSpinner } from "react-icons/fa";
import { showToast } from "../utils/utils";
import publicAxios from "../utils/publicAxios";
import NotFound from "./NotFound";
import {
  buttonName,
  commonErrMsg,
  createAccPage,
  login,
  mail,
  myAccount,
  signAgreementContent
} from "../locales/en";
import {
  FILE_SIZE_LIMIT,
  INFO_EMAIL,
  MAILTO_INFO_EMAIL
} from "../utils/constants/appConstants";
import { showToastType } from "../utils/constants/appConstants";
import {
  getAgreementUploadUrlLaterIDUrl,
  getLeagalAgreementGenerateDownloadTokenUrl,
  getLeagalAgreementUploadUrl,
  getTrustCorporateGuaranteeFormURL
} from "../utils/constants/apiEndPoints";

const AccountGuarntee = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState();
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [isUploadLater, setIsUploadLater] = useState(false);
  const fileInput = useRef(null);
  const [tokenFlag, setTokenFlag] = useState();
  const [userAccountId, setUserAccountId] = useState();
  const [holdToken, setHoldToken] = useState();

  // load Data ===
  const loadGuranteeData = useCallback(async (token) => {
    setLoading(true);
    try {
      const response = await publicAxios.post(
        getTrustCorporateGuaranteeFormURL,
        {},
        {
          params: {
            token: token
          }
        }
      );
      setData(response.data.data);
      setTokenFlag(true);
      setUserAccountId(response?.data?.data?.Tc_file_data?.userAccountId);
    } catch {
      setTokenFlag(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDownload = async (event) => {
    if (event) event.preventDefault();
    try {
      setLoading(true);
      const response = await publicAxios.get(
        getLeagalAgreementGenerateDownloadTokenUrl,
        {
          params: { token: holdToken },
          responseType: "arraybuffer"
        }
      );

      // Create a Blob from the response data
      const blob = new Blob([response.data], { type: "application/pdf" });

      // Create a link element
      const link = document.createElement("a");

      // Prefer the server's filename (it carries the document type/version);
      // fall back to a generic name when the header is absent.
      const contentDisposition = response.headers?.["content-disposition"];
      const filenameMatch = contentDisposition?.match(/filename="?([^";]+)"?/);
      const filename = filenameMatch?.[1] || "FundTap Deed.pdf";
      link.href = URL.createObjectURL(blob);
      link.download = filename;

      // Trigger the download by simulating a click on the link
      link.click();

      // Clean up the object URL
      URL.revokeObjectURL(link.href);
    } catch {
      showToast(commonErrMsg.item11, showToastType.error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    const { files } = event.target;

    // Ensure a file is selected
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];

    if (file.size > FILE_SIZE_LIMIT) {
      showToast(createAccPage.errMsg.item1, showToastType.error);
      event.target.value = "";
      setFileName("");
      return;
    }

    setFileName(file.name);

    try {
      setLoadingUpload(true);

      const formDataToSend = new FormData();
      formDataToSend.append("file", file, file.name);

      const config = {
        method: "post",
        url: getLeagalAgreementUploadUrl(holdToken),
        headers: { "Content-Type": "multipart/form-data" },
        data: formDataToSend
      };

      const response = await publicAxios(config);

      if (response.status === 200) {
        showToast(response.data.message, showToastType.success);
      }
    } catch (error) {
      if (error.response) {
        showToast(error.response.data.message, showToastType.error);
      }
    } finally {
      setLoadingUpload(false);
    }
  };

  const handleUploadButtonClick = () => {
    fileInput.current.click();
  };

  const handleCheckboxChange = async (e) => {
    const { checked, type, value } = e.target;

    const newValue = type === "checkbox" ? checked : value;

    setIsUploadLater(newValue);

    try {
      const response = await publicAxios.post(
        getAgreementUploadUrlLaterIDUrl(userAccountId),
        {},
        {
          params: {
            isUploadLater: newValue
          }
        }
      );

      if (response.status === 200) {
        showToast(response.data.message, showToastType.success);
      }
    } catch (error) {
      showToast(error.response.data.message, showToastType.error);
    }
  };

  useEffect(() => {
    const token = searchParams.get("token");
    loadGuranteeData(token);
    setHoldToken(token);
  }, [searchParams, loadGuranteeData]);

  if (loading) {
    return (
      <div className="spinner-wrap bg-light">
        <div className="spinner-border custom-spinner-color" />
      </div>
    );
  }

  if (!tokenFlag) {
    return <NotFound />;
  }

  return (
    <>
      <HeaderDashboard />

      <LoginAsUser />
      <div className="home-main-section py-5">
        <div className="container">
          <div className="home-title">
            <h1>{myAccount.main_title}</h1>
          </div>
          <div className="row justify-content-center mt-4">
            <div className="fundtap_Guarantee col-12 col-md-7">
              <div className="fundtap_Guarantee_text">
                <h3>{`${login.mainTitle2} ${data?.heading_text}`}</h3>
                <div className="conditional-texts">
                  {data?.Tc_file_data?.isDocumentSigned === true && (
                    <div className="texts-items mt-4">
                      <p>
                        <FaCheckCircle />
                        {signAgreementContent.titles.sub}
                        {data?.heading_text}
                        {signAgreementContent.titles.sub1}
                        <br />

                        {signAgreementContent.email.contact}
                        <br />
                        {signAgreementContent.email.address}
                        <Link to={MAILTO_INFO_EMAIL}>{mail}</Link>
                      </p>
                    </div>
                  )}
                  {data?.Tc_file_data?.annatureEnvelopeStatus ===
                    "completed" && (
                    <div className="texts-items mt-4 ">
                      <p>
                        <FaCheckCircle />
                        {signAgreementContent.email.annature}
                        {data?.heading_text}
                        {signAgreementContent.titles.annatureSub}
                        <br />
                        {signAgreementContent.email.contact}
                        <br />
                        {signAgreementContent.email.address}
                        <Link to={MAILTO_INFO_EMAIL}>{INFO_EMAIL}</Link>
                      </p>
                    </div>
                  )}
                  {data?.annature_recipient_status[0]?.status ===
                    "completed" && (
                    <div className="texts-items mt-4 ">
                      <p>
                        <FaCheckCircle />
                        {signAgreementContent.titles.annature}
                        {data?.heading_text}
                        {signAgreementContent.titles.annatureSub}
                        <br />
                        {signAgreementContent.email.contact}
                        <br />
                        {signAgreementContent.email.address}
                        <Link to={MAILTO_INFO_EMAIL}>{mail}</Link>
                      </p>
                    </div>
                  )}

                  {data?.Tc_file_data === null && (
                    <div>
                      <p>
                        {signAgreementContent.titles.manual}
                        {data?.heading_text}

                        {signAgreementContent.titles.heading}
                      </p>
                      <div className="signBtnGrp my-4">
                        <button type="button" className="btn btn-primary">
                          {signAgreementContent.buttons.digitalSign}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-transparent"
                        >
                          {signAgreementContent.buttons.manualUpload}
                        </button>
                      </div>
                    </div>
                  )}

                  <p className="my-4">
                    {signAgreementContent.titles.instructions}
                  </p>

                  {data?.Tc_file_data?.documentType === "Trust_Guarantee" && (
                    <ul>
                      <li>{`${signAgreementContent.instructions.TrustGuarantee1} ${data?.heading_text}.`}</li>
                      <li>
                        {signAgreementContent.instructions.TrustGuarantee2}
                      </li>
                      <li>
                        {signAgreementContent.instructions.TrustGuarantee3}
                      </li>
                      <li>
                        {signAgreementContent.instructions.TrustGuarantee4}
                      </li>
                    </ul>
                  )}
                  {data?.Tc_file_data?.documentType ===
                    "Corporate_Guarantee" && (
                    <ul>
                      <li>{`${signAgreementContent.instructions.TrustGuarantee1} ${data?.heading_text}.`}</li>
                      <li>
                        {signAgreementContent.instructions.TrustGuarantee2}
                      </li>
                      <li>
                        {signAgreementContent.instructions.TrustGuarantee3}
                      </li>
                      <li>
                        {signAgreementContent.instructions.TrustGuarantee4}
                      </li>
                    </ul>
                  )}
                  {data?.Tc_file_data?.documentType !== "Corporate_Guarantee" &&
                    data?.Tc_file_data?.documentType !== "Trust_Guarantee" && (
                      <ul>
                        <li>
                          {signAgreementContent.instructions.TrustGuarantee5}
                        </li>
                        <li>
                          {signAgreementContent.instructions.TrustGuarantee3}
                        </li>
                        <li>
                          {signAgreementContent.instructions.TrustGuarantee4}
                        </li>
                      </ul>
                    )}
                </div>
                <p>
                  {signAgreementContent.email.help}
                  <a href={MAILTO_INFO_EMAIL}>{INFO_EMAIL}</a>
                </p>
              </div>
              <div className="agreement-group-warp fundtap_Guarantee_btngrp">
                <div className="row mb-4">
                  <div className="col-md-6 d-flex pt-2 pb-2 align-items-center">
                    <label htmlFor="Download">
                      {`Download ${data?.heading_text}`}
                    </label>
                  </div>
                  <div className="col-md-6">
                    <Link
                      className={`btn btn-primary fundtap_agreement spinDiv`}
                      id="Download"
                      onClick={handleDownload}
                    >
                      {"Download"}
                      {loading && (
                        <span className="loadSpin">
                          <FaSpinner />
                        </span>
                      )}
                    </Link>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 d-flex pt-2 pb-2 align-items-center">
                    <label htmlFor="">
                      {`Upload Signed ${data?.heading_text}`}
                    </label>
                  </div>
                  <div className="col-md-6">
                    <div className="upload-file">
                      {fileName && (
                        <p className="uploaded-file-name">{fileName}</p>
                      )}
                      <button
                        type="button"
                        onClick={handleUploadButtonClick}
                        className={`btn btn-primary fundtap_agreement spinDiv `}
                      >
                        {buttonName.upload}
                        {loadingUpload && (
                          <span className="loadSpin">
                            <FaSpinner />
                          </span>
                        )}
                      </button>
                      <input
                        name="isFile"
                        type="file"
                        ref={fileInput}
                        onChange={handleFileChange}
                        className="d-none"
                      />
                    </div>
                  </div>
                </div>
                {data?.Tc_file_data?.documentType !== "Corporate_Guarantee" &&
                  data?.Tc_file_data?.documentType !== "Trust_Guarantee" && (
                    <div className="row mt-2">
                      <div className="col-md-6 offset-md-6">
                        <div className="upload-checkbox d-flex justify-content-end">
                          <label htmlFor="upload" className="me-1">
                            {signAgreementContent.labels.uploadLater}
                          </label>
                          <input
                            type="checkbox"
                            id="upload"
                            checked={isUploadLater}
                            value={isUploadLater}
                            onChange={handleCheckboxChange}
                          />
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountGuarntee;
